import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

const Navbar = () => {
    const { isDarkTheme } = useTheme();
    const [showDropdown, setShowDropdown] = useState(false);
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
    const { user, profile, signOut: supabaseSignOut } = useAuth();
    const navigate = useNavigate();
    const { t } = useLanguage();

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 20;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrolled]);

    // Close mobile menu on resize to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768) {
                setMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [mobileMenuOpen]);

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    const handleSignOut = async () => {
        try {
            console.log('Sign-out initiated from Navbar');
            setShowDropdown(false);
            setMobileMenuOpen(false);
            await supabaseSignOut();
            console.log('Sign-out completed, navigating to login');
            // Small delay to ensure auth state clears
            setTimeout(() => {
                navigate('/login', { replace: true });
            }, 100);
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const toggleDropdown = () => {
        setShowDropdown(!showDropdown);
    };


    return (
        <>
            <nav className={`navbar ${isDarkTheme ? 'dark' : 'light'} ${scrolled ? 'scrolled' : ''}`}>
                <div className="nav-brand">
                    <Link to="/">
                        <div className="brand-container">
                            <img
                                src={isDarkTheme ? "/image.png" : "/math-light.png"}
                                alt="Logo"
                                className="nav-logo"
                            />
                            <span className="brand-text">MathBox</span>
                        </div>
                    </Link>
                </div>

                <div className="nav-menu-items">
                    <Link to="/units">Units</Link>
                    <Link to="/practice">Practice</Link>
                    <Link to="/learn">Lessons</Link>
                    <Link to="/products">Products <span className="chevron">⌄</span></Link>
                    <Link to="/resources">Resources <span className="chevron">⌄</span></Link>
                    <Link to="/docs">Docs</Link>
                    <div 
                        className="nav-dropdown-container"
                        onMouseEnter={() => setShowCompanyDropdown(true)}
                        onMouseLeave={() => setShowCompanyDropdown(false)}
                    >
                        <span className="nav-dropdown-trigger">Company <span className="chevron">⌄</span></span>
                        {showCompanyDropdown && (
                            <div className="nav-dropdown">
                                <Link to="/about" onClick={() => setShowCompanyDropdown(false)}>About</Link>
                                <Link to="/careers" onClick={() => setShowCompanyDropdown(false)}>Careers</Link>
                            </div>
                        )}
                    </div>
                    <Link to="/pricing">Pricing</Link>
                </div>

                <div className="nav-actions">
                    {user ? (
                        <div className="user-profile">
                            <div className="profile-trigger" onClick={toggleDropdown}>
                                {profile?.avatar_url ? (
                                    <img
                                        src={profile.avatar_url}
                                        alt="Profile"
                                        className="profile-photo"
                                    />
                                ) : (
                                    <div className="profile-initial">
                                        {(profile?.display_name || user.email || 'U').charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <span className="user-name">
                                    {profile?.display_name || user.user_metadata?.display_name || user.email?.split('@')[0]}
                                </span>
                            </div>

                            {showDropdown && (
                                <div className="profile-dropdown">
                                    <Link to="/dashboard" onClick={() => setShowDropdown(false)}>{t('nav.dashboard')}</Link>
                                    {profile?.role && ['admin', 'reviewer', 'writer'].includes(profile.role) && (
                                        <Link to="/admin" onClick={() => setShowDropdown(false)}>Admin Panel</Link>
                                    )}
                                    <Link to="/profile" onClick={() => setShowDropdown(false)}>{t('nav.profile')}</Link>
                                    <button onClick={handleSignOut} className="sign-out-btn">{t('nav.signOut')}</button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Link to="/signup" className="signup-btn">Sign up</Link>
                    )}

                    {/* Hamburger Menu Button */}
                    <button 
                        className={`hamburger ${mobileMenuOpen ? 'active' : ''}`} 
                        onClick={toggleMobileMenu}
                        aria-label="Toggle menu"
                    >
                        <span className="hamburger-line"></span>
                        <span className="hamburger-line"></span>
                        <span className="hamburger-line"></span>
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <div 
                className={`mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`} 
                onClick={closeMobileMenu}
            ></div>

            {/* Mobile Menu */}
            <div className={`mobile-menu ${isDarkTheme ? 'dark' : ''} ${mobileMenuOpen ? 'active' : ''}`}>
                <div className="mobile-menu-links">
                    <Link to="/units" onClick={closeMobileMenu}>Units</Link>
                    <Link to="/practice" onClick={closeMobileMenu}>Practice</Link>
                    <Link to="/learn" onClick={closeMobileMenu}>Lessons</Link>
                    <Link to="/products" onClick={closeMobileMenu}>Products <span className="chevron">⌄</span></Link>
                    <Link to="/resources" onClick={closeMobileMenu}>Resources <span className="chevron">⌄</span></Link>
                    <Link to="/docs" onClick={closeMobileMenu}>Docs</Link>
                    <div 
                        className={`mobile-dropdown-trigger ${mobileCompanyOpen ? 'open' : ''}`}
                        onClick={() => setMobileCompanyOpen(!mobileCompanyOpen)}
                    >
                        Company <span className={`chevron ${mobileCompanyOpen ? 'open' : ''}`}>⌄</span>
                    </div>
                    {mobileCompanyOpen && (
                        <div className="mobile-dropdown">
                            <Link to="/about" onClick={closeMobileMenu}>About</Link>
                            <Link to="/careers" onClick={closeMobileMenu}>Careers</Link>
                        </div>
                    )}
                    <Link to="/pricing" onClick={closeMobileMenu}>Pricing</Link>
                </div>
                
                <div className="mobile-menu-actions">
                    {user ? (
                        <>
                            <Link to="/dashboard" onClick={closeMobileMenu} className="btn-outline">{t('nav.dashboard')}</Link>
                            {profile?.role && ['admin', 'reviewer', 'writer'].includes(profile.role) && (
                                <Link to="/admin" onClick={closeMobileMenu} className="btn-outline">Admin Panel</Link>
                            )}
                            <Link to="/profile" onClick={closeMobileMenu} className="btn-primary">{t('nav.profile')}</Link>
                        </>
                    ) : (
                        <>
                            <Link to="/login" onClick={closeMobileMenu} className="btn-outline">Log in</Link>
                            <Link to="/signup" onClick={closeMobileMenu} className="btn-primary">Sign up</Link>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default Navbar;